import type { AutorDaMensagem } from './contrato';

/**
 * Prazo de resposta da SME a uma mensagem do credenciado.
 *
 * Política do protótipo: a SME tem 3 dias úteis para responder quando a última
 * mensagem da conversa é do credenciado. Se a última é da SME (ou de DTP e
 * admin, que falam pelo mesmo lado), não há prazo correndo. Dia útil aqui é
 * dia de semana — feriado ainda não entra, como no protótipo; a regra que
 * considera o calendário de dias letivos vem com a rota real.
 *
 * O valor é parametrizável em `/admin/configuracoes/slas`, mas essa tela ainda
 * não grava; até lá a constante é a fonte.
 */
export const SLA_RESPOSTA_SME_DIAS_UTEIS = 3;

export type SituacaoDoPrazo = 'NO_PRAZO' | 'VENCENDO' | 'VENCIDO' | 'SEM_PRAZO';

export interface PrazoDeResposta {
  situacao: SituacaoDoPrazo;
  /** Dias úteis já estourados. Só faz sentido em `VENCIDO`. */
  diasAtraso: number;
}

function fimDeSemana(data: Date): boolean {
  const dia = data.getDay();
  return dia === 0 || dia === 6;
}

function somarDiasUteis(base: Date, dias: number): Date {
  const resultado = new Date(base);
  let restante = dias;
  while (restante > 0) {
    resultado.setDate(resultado.getDate() + 1);
    if (!fimDeSemana(resultado)) {
      restante -= 1;
    }
  }
  return resultado;
}

/** Dias úteis de `a` até `b` — negativo quando `b` é anterior a `a`. */
function diasUteisEntre(a: Date, b: Date): number {
  const sinal = b.getTime() >= a.getTime() ? 1 : -1;
  const cursor = new Date(sinal === 1 ? a : b);
  const fim = new Date(sinal === 1 ? b : a);
  cursor.setHours(0, 0, 0, 0);
  fim.setHours(0, 0, 0, 0);
  let contagem = 0;
  while (cursor < fim) {
    cursor.setDate(cursor.getDate() + 1);
    if (!fimDeSemana(cursor)) {
      contagem += 1;
    }
  }
  return sinal * contagem;
}

export function prazoDeResposta(
  ultimaMensagemEm: string,
  ultimoAutorPapel: AutorDaMensagem,
  agora: Date = new Date(),
  diasUteis: number = SLA_RESPOSTA_SME_DIAS_UTEIS,
): PrazoDeResposta {
  if (ultimoAutorPapel !== 'CREDENCIADO') {
    return { situacao: 'SEM_PRAZO', diasAtraso: 0 };
  }

  const vencimento = somarDiasUteis(new Date(ultimaMensagemEm), diasUteis);
  const restantes = diasUteisEntre(agora, vencimento);

  if (restantes < 0) {
    return { situacao: 'VENCIDO', diasAtraso: Math.abs(restantes) };
  }
  if (restantes <= 1) {
    return { situacao: 'VENCENDO', diasAtraso: 0 };
  }
  return { situacao: 'NO_PRAZO', diasAtraso: 0 };
}
